import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/vanilla/utils';
import { CookieUtils } from '@framework/CookieUtils';
import { authStateAtom } from './authStateAtom';
import { useAuthStatus } from './authStatus';
import { SignUpModeCookieName, getAdjustedSignUpMode } from './signUpMode';
import type { SignUpMode } from './signUpMode';

/**
 * pendingRegistration 状態(Firebase 認証済み・Balus ユーザー未作成)が保持する、
 * 登録確定前の一時情報。
 *
 * Firebase ユーザーの情報(uid/email/displayName)と、登録モード(signUpMode)・
 * 招待トークンを保持し、後続の登録フロー(signUpWithFirebaseAuth)の入力に用いる。
 */
export type RegistrationDraft = {
  firebaseUid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  signUpMode: SignUpMode;
  invitationToken: string | null;
};

/**
 * pendingRegistration 状態の一時情報({@link RegistrationDraft})だけを購読する派生 atom。
 * authStatus / loading の変化では発火しないため、無駄な再描画を防ぐ。
 */
export const registrationDraftAtom = selectAtom(authStateAtom, (state) => state.registrationDraft);

/**
 * pendingRegistration 状態に入る際の {@link RegistrationDraft} を構築する。
 * Firebase ユーザーの情報と、Cookie に保存された signUpMode から組み立てる。
 * (signUpMode を Cookie から読み取るため純粋関数ではない点に注意)
 *
 * 招待トークン(invitationToken)の保存元 Cookie は後続の UI 配線で確定するため、
 * 現状は null 固定としている。
 */
export const buildRegistrationDraft = (firebaseUser: {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
}): RegistrationDraft => {
  return {
    firebaseUid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    emailVerified: firebaseUser.emailVerified,
    signUpMode: getAdjustedSignUpMode(CookieUtils.get(SignUpModeCookieName) ?? null),
    invitationToken: null,
  };
};

/**
 * 登録確定前の一時情報({@link RegistrationDraft})を返すフック。
 * pendingRegistration でない場合は null を返す。
 */
export const usePendingRegistration = (): RegistrationDraft | null => {
  return useAtomValue(registrationDraftAtom);
};

/**
 * 認証状態が pendingRegistration(Firebase 認証済み・Balus ユーザー未作成)か否かを返すフック。
 */
export const useIsPendingRegistration = (): boolean => {
  return useAuthStatus().status === 'pendingRegistration';
};
